// Small building blocks for the daily-cycle screens. Built here first (ui.tsx is
// integrator-owned); every row and option is a whole-width tap target of at least 44px.
import type { ReactNode } from 'react'
import { useId } from 'react'
import { DateTimeInput } from '@/components/DateTimeInput'
import { RuleRefs, SignatureLine } from '@/components/evidence'
import { Badge, Banner, Button, cx, Field, TextInput } from '@/components/ui'
import { CYCLE_STATES, ROLES } from '@/domain/catalogue'
import type { AttestationView, AvailableAction, CycleView, GasTestView, PersonRef } from '@/domain/types'
import { fmtDateTime } from '@/lib/format'
import { formatDuration, minutesBetween, type PolicyParameter, VALUE_STATUS_TEXT } from './logic'

export function roleLabel(role: string | null | undefined): string {
  if (!role) return '-'
  return ROLES[role as keyof typeof ROLES]?.label ?? role
}

/** Tick or cross with words, so the meaning never depends on colour. Long words may wrap. */
export function Mark({ ok, yes = 'Yes', no = 'No' }: { ok: boolean; yes?: string; no?: string }) {
  return ok ? (
    <span className="font-semibold text-ok">
      <span aria-hidden>&#10003;&nbsp;</span>
      {yes}
    </span>
  ) : (
    <span className="font-semibold text-bad">
      <span aria-hidden>&#10007;&nbsp;</span>
      {no}
    </span>
  )
}

export function CycleStateBadge({ state }: { state: string }) {
  const st = CYCLE_STATES[state] ?? { label: state.replace(/_/g, ' '), tone: 'neutral' as const }
  return <Badge tone={st.tone}>{st.label}</Badge>
}

/** One deliberate tick; the whole row is the tap target. */
export function TickRow({ checked, onChange, label, hint, disabled }: {
  checked: boolean
  onChange: (v: boolean) => void
  label: ReactNode
  hint?: ReactNode
  disabled?: boolean
}) {
  return (
    <label className={cx('flex min-h-[44px] items-start gap-3 px-3 py-2.5', disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer')}>
      <input type="checkbox" className="mt-0.5 shrink-0" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm text-ink">
        {label}
        {hint ? <span className="mt-0.5 block text-xs text-ink-soft">{hint}</span> : null}
      </span>
    </label>
  )
}

/** A list of statements the signer must tick one by one. */
export function TickList({ title, options, value, onChange, disabled, ruleIds }: {
  title: ReactNode
  options: readonly { code: string; label: string; help?: string }[]
  value: Record<string, boolean>
  onChange: (next: Record<string, boolean>) => void
  disabled?: boolean
  ruleIds?: string[]
}) {
  const ticked = options.filter((o) => value[o.code] === true).length
  return (
    <fieldset className="mb-4">
      <legend className="mb-1 text-sm font-semibold text-ink">
        {title}
        <RuleRefs ruleIds={ruleIds} />
      </legend>
      <p className="mb-2 text-xs text-ink-soft" aria-live="polite">
        {ticked} of {options.length} ticked
      </p>
      <div className="divide-y divide-line rounded-lg border border-line bg-white">
        {options.map((o) => (
          <TickRow
            key={o.code}
            checked={value[o.code] === true}
            disabled={disabled}
            onChange={(v) => onChange({ ...value, [o.code]: v })}
            label={o.label}
            hint={o.help}
          />
        ))}
      </div>
    </fieldset>
  )
}

/** Segmented single choice: each option is a labelled radio at least 44px high. */
export function Choice<T extends string>({ legend, hint, name, options, value, onChange, compact }: {
  legend: ReactNode
  hint?: ReactNode
  name: string
  options: readonly { value: T; label: string }[]
  value: T | null
  onChange: (v: T) => void
  compact?: boolean
}) {
  return (
    <fieldset>
      <legend className={cx('text-sm text-ink', compact ? '' : 'font-semibold')}>{legend}</legend>
      {hint ? <p className="mt-0.5 text-xs text-ink-soft">{hint}</p> : null}
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((o) => {
          const selected = value === o.value
          return (
            <label
              key={o.value}
              className={cx(
                'flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm font-semibold',
                selected ? 'border-brand bg-brand-light text-brand-dark' : 'border-line-strong bg-white text-ink',
              )}
            >
              <input type="radio" name={name} checked={selected} onChange={() => onChange(o.value)} />
              {o.label}
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

export function YesNo({ legend, hint, name, value, onChange }: {
  legend: ReactNode
  hint?: ReactNode
  name: string
  value: boolean | null
  onChange: (v: boolean) => void
}) {
  return (
    <Choice
      legend={legend}
      hint={hint}
      name={name}
      compact
      options={[
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ]}
      value={value === null ? null : value ? 'yes' : 'no'}
      onChange={(v) => onChange(v === 'yes')}
    />
  )
}

/** Day/month/year and 24-hour time in Muscat time (the zone is always named; src/components/DateTimeInput.tsx). */
export function DateTimeField({ label, value, onChange, hint, required, error }: {
  label: ReactNode
  value: string
  onChange: (v: string) => void
  hint?: ReactNode
  required?: boolean
  error?: ReactNode
}) {
  const id = useId()
  return (
    <Field
      htmlFor={id}
      required={required}
      hint={hint}
      error={error}
      label={
        <>
          {label} <span className="font-normal text-ink-soft">(GST, Muscat time)</span>
        </>
      }
    >
      <DateTimeInput id={id} name={typeof label === 'string' ? label : undefined} value={value} onChange={onChange} />
    </Field>
  )
}

/** Plain text box with a visible label (the Field kit wires the label to the input). */
export function TextField({ label, value, onChange, hint, required, placeholder, inputMode }: {
  label: ReactNode
  value: string
  onChange: (v: string) => void
  hint?: ReactNode
  required?: boolean
  placeholder?: string
  inputMode?: 'text' | 'decimal'
}) {
  const id = useId()
  return (
    <Field htmlFor={id} label={label} hint={hint} required={required}>
      <TextInput id={id} value={value} placeholder={placeholder} inputMode={inputMode} onChange={(e) => onChange(e.target.value)} />
    </Field>
  )
}

/** The declaration a person makes by signing, in their own words, with who is signing. */
export function SigningStatement({ role, person, children }: { role: string; person: PersonRef; children: ReactNode }) {
  return (
    <section className="mb-4 rounded-lg border-2 border-brand/40 bg-white p-3" aria-label="What you are signing">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">What you are signing</p>
      <div className="mt-1 space-y-1 text-sm text-ink">{children}</div>
      <p className="mt-2 text-xs text-ink-soft">
        You sign as <strong>{person.display_name}</strong>, {roleLabel(role)}. Your name and the signing time come from your
        login and the server clock, not from this device.
      </p>
    </section>
  )
}

/**
 * Sticky action bar at the bottom of the sheet. The Sheet's scroll box has p-4, and a
 * sticky edge is measured inside that padding, so the bar sits at -1rem (and spans the
 * side padding) to stay flush with the bottom edge with nothing showing beneath it.
 */
export function PanelFooter({ onCancel, onSubmit, submitLabel, pending, disabled, danger, note }: {
  onCancel: () => void
  onSubmit: () => void
  submitLabel: string
  pending?: boolean
  disabled?: boolean
  danger?: boolean
  note?: ReactNode
}) {
  return (
    <div className="sticky -bottom-4 -mx-4 mt-4 border-t border-line bg-paper px-4 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))]">
      {note ? <p className="mb-2 text-xs text-ink-soft">{note}</p> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" variant={danger ? 'danger' : 'primary'} onClick={onSubmit} loading={pending} disabled={disabled}>
          {submitLabel}
        </Button>
      </div>
    </div>
  )
}

/** What is still missing in the form (shown after the first attempt to sign). */
export function FormProblems({ problems }: { problems: string[] }) {
  if (problems.length === 0) return null
  return (
    <Banner tone="warn" title="Before you can send this" className="mb-4">
      <ul className="list-disc space-y-1 pl-5">
        {problems.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </Banner>
  )
}

/** The server listed this step for the viewer but reports it is not available now. */
export function ActionUnavailable({ action }: { action: AvailableAction }) {
  if (action.enabled) return null
  return (
    <Banner tone="warn" title="You cannot do this step now" className="mb-4">
      {action.reason || 'The server reports that this step is not open to you at the moment.'}
    </Banner>
  )
}

/** A rule value with its status, so a pending or synthetic value never reads as approved policy. */
export function PolicyNote({ param, code }: { param: PolicyParameter | null; code: string }) {
  if (!param) {
    return <span className="text-xs text-ink-soft">(rule value {code} not loaded)</span>
  }
  return (
    <span className="text-xs text-ink-soft">
      ({VALUE_STATUS_TEXT[param.value_status] ?? param.value_status})
      <RuleRefs ruleIds={param.rule_ids} decisionIds={param.decision_ids} />
    </span>
  )
}

export function WindowText({ from, to }: { from: string | null | undefined; to: string | null | undefined }) {
  const mins = minutesBetween(from, to)
  return (
    <>
      {fmtDateTime(from)} to {fmtDateTime(to)}
      {mins !== null ? <span className="text-ink-soft"> ({formatDuration(mins)})</span> : null}
    </>
  )
}

/** Header shown at the top of every cycle action: which cycle this signature is for. */
export function CycleHeader({ cycle }: { cycle: CycleView }) {
  return (
    <div className="mb-4 rounded-lg border border-line bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="display text-base font-semibold text-ink">Cycle {cycle.cycle_number}</span>
        <CycleStateBadge state={cycle.cycle_state} />
      </div>
      <p className="mt-1 text-sm text-ink">
        Requested window: <WindowText from={cycle.requested_from} to={cycle.requested_to} />
      </p>
      <p className="mt-1 text-xs text-ink-soft">Anything you sign here counts for cycle {cycle.cycle_number} only.</p>
    </div>
  )
}

export function MissingCycle() {
  return (
    <Banner tone="bad" title="Cycle not found" className="mb-4">
      This cycle is not on the permit as loaded. Close this panel and refresh the permit.
    </Banner>
  )
}

const CLASSIFICATION: Record<GasTestView['classification'], { tone: 'ok' | 'bad' | 'warn'; glyph: string; label: string }> = {
  acceptable: { tone: 'ok', glyph: '✓', label: 'Acceptable' },
  unsafe: { tone: 'bad', glyph: '✗', label: 'Unsafe - work stopped' },
  unresolved: { tone: 'warn', glyph: '?', label: 'Unresolved - no approved limits' },
}

const BASIS_TEXT: Record<string, string> = {
  agt_declared_against_reference: "tester's declaration",
  numeric_policy: 'approved numeric limits',
  none: 'no basis',
}

function classificationOf(test: Pick<GasTestView, 'classification' | 'classification_basis'>) {
  const c = CLASSIFICATION[test.classification] ?? { tone: 'warn' as const, glyph: '?', label: test.classification }
  const basis = test.classification === 'acceptable' ? (BASIS_TEXT[test.classification_basis] ?? test.classification_basis) : null
  return { ...c, basis }
}

export function GasClassificationBadge({ test }: { test: Pick<GasTestView, 'classification' | 'classification_basis'> }) {
  const c = classificationOf(test)
  return (
    <Badge tone={c.tone}>
      <span aria-hidden>{c.glyph}</span>
      {c.label}
      {c.basis ? ` (${c.basis})` : ''}
    </Badge>
  )
}

/** The same classification as plain text, for tight spaces. */
export function GasClassificationText({ test }: { test: Pick<GasTestView, 'classification' | 'classification_basis'> }) {
  const c = classificationOf(test)
  const colour = c.tone === 'ok' ? 'text-ok' : c.tone === 'bad' ? 'text-bad' : 'text-warn'
  return (
    <span>
      <span className={cx('font-semibold', colour)}>
        <span aria-hidden>{c.glyph}&nbsp;</span>
        {c.label}
      </span>
      {c.basis ? <span className="text-ink-soft"> ({c.basis})</span> : null}
    </span>
  )
}

/** A compact signed / not signed line for lists (attendees). */
export function SignedState({ a }: { a: AttestationView | null }) {
  if (!a) {
    return (
      <span className="text-xs font-semibold text-warn">
        <span aria-hidden>&#9675; </span>Not signed yet
      </span>
    )
  }
  return (
    <span className="text-xs text-ink-soft">
      <span className="font-semibold text-ok">
        <span aria-hidden>&#10003; </span>Signed
      </span>{' '}
      {fmtDateTime(a.server_signed_at)}
    </span>
  )
}

/** Signature with a label that names the cycle it belongs to. */
export function CycleSignature({ a, what, cycleNumber }: { a: AttestationView | null | undefined; what: string; cycleNumber: number }) {
  return <SignatureLine a={a} label={`${what} - cycle ${cycleNumber} only`} />
}
